import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import { CONTACT_EMAIL, CLUBS, IMAGES } from '../data/mock';

const EMPTY_FORM = {
  name: '',
  birth_date: '',
  email: '',
  phone: '',
  address: '',
  club: 'toulouse',
  preferred_days: '',
  message: '',
};

// Pure validation logic (kept out of the component to lower complexity)
export function validateLead(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Ton nom et prénom sont requis';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Email invalide';
  if (!form.phone.trim()) errors.phone = 'Le téléphone est requis';
  return errors;
}

const baseField =
  'w-full bg-[#131313] border rounded-md px-4 py-3.5 text-white placeholder:text-[#666] focus:outline-none focus:border-accent transition-colors';

function Field({ label, name, error, children }) {
  return (
    <div>
      <label className="font-cond text-xs tracking-widest text-[var(--hx-muted)] block mb-2">{label}</label>
      {children}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function ContactHero() {
  return (
    <section className="relative pt-32 pb-16 md:pt-52 md:pb-24 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.heroSled2} alt="" loading="eager" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/90 to-[#0a0a0a]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <p className="hx-eyebrow mb-4">Séance d'essai offerte</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.92] max-w-3xl">
          Réserve ta <span className="text-accent">première séance</span>
        </h1>
        <p className="text-[var(--hx-muted)] text-lg max-w-xl mt-5">
          Laisse-nous tes infos, un coach te recontacte sous 24h pour caler ton créneau. C'est gratuit et sans engagement.
        </p>
      </div>
    </section>
  );
}

function SuccessView({ firstName, onReset }) {
  return (
    <div className="text-center py-12">
      <CheckCircle2 size={56} className="text-accent mx-auto mb-5" />
      <h3 className="font-display text-3xl mb-3">Demande envoyée !</h3>
      <p className="text-[var(--hx-muted)] max-w-md mx-auto">
        Merci {firstName} ! On a bien reçu ta demande. Un coach te recontacte très vite pour ta séance d'essai.
      </p>
      <button onClick={onReset} className="hx-btn hx-btn-outline mt-7">Nouvelle demande</button>
    </div>
  );
}

function LeadForm({ form, errors, status, onChange, onSubmit }) {
  const cls = (name) => `${baseField} ${errors[name] ? 'border-red-500/70' : 'border-[#262626]'}`;
  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="NOM ET PRÉNOM *" name="name" error={errors.name}>
          <input name="name" value={form.name} onChange={onChange} className={cls('name')} placeholder="Jean Dupont" />
        </Field>
        <Field label="DATE DE NAISSANCE" name="birth_date">
          <input type="date" name="birth_date" value={form.birth_date} onChange={onChange} className={`${cls('birth_date')} [color-scheme:dark]`} />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="EMAIL *" name="email" error={errors.email}>
          <input name="email" value={form.email} onChange={onChange} className={cls('email')} placeholder="jean@email.com" />
        </Field>
        <Field label="TÉLÉPHONE *" name="phone" error={errors.phone}>
          <input name="phone" value={form.phone} onChange={onChange} className={cls('phone')} placeholder="06 12 34 56 78" />
        </Field>
      </div>
      <Field label="ADRESSE POSTALE" name="address">
        <input name="address" value={form.address} onChange={onChange} className={cls('address')} placeholder="12 rue du Sport, 31000 Toulouse" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="SALLE CONCERNÉE" name="club">
          <select name="club" value={form.club} onChange={onChange} className={cls('club')}>
            {Object.values(CLUBS).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="JOUR(S) D'ESSAI PRÉFÉRÉ(S)" name="preferred_days">
          <input name="preferred_days" value={form.preferred_days} onChange={onChange} className={cls('preferred_days')} placeholder="Ex : mardi soir ou samedi matin" />
        </Field>
      </div>
      <Field label="TON MESSAGE (optionnel)" name="message">
        <textarea name="message" value={form.message} onChange={onChange} rows={3} className={cls('message')} placeholder="Dis-nous en plus sur tes objectifs..." />
      </Field>
      <button type="submit" disabled={status === 'sending'} className="hx-btn w-full justify-center text-base disabled:opacity-70">
        {status === 'sending' ? (<><Loader2 size={18} className="animate-spin" /> Envoi en cours...</>) : (<>Envoyer ma demande <Send size={17} /></>)}
      </button>
      {status === 'error' && (
        <p className="text-red-400 text-sm text-center">Une erreur est survenue. Réessaie ou écris-nous directement à {CONTACT_EMAIL}.</p>
      )}
      <p className="text-[var(--hx-muted)] text-xs text-center">En envoyant ce formulaire, tu acceptes d'être recontacté par notre équipe.</p>
    </form>
  );
}

function ContactInfo() {
  return (
    <div className="space-y-4">
      <a href={`mailto:${CONTACT_EMAIL}`} className="hx-card rounded-lg p-6 flex items-center gap-4 group">
        <span className="w-12 h-12 grid place-items-center bg-accent/10 border border-accent/30 rounded-md shrink-0"><Mail size={20} className="text-accent" /></span>
        <div>
          <p className="font-cond text-xs tracking-widest text-[var(--hx-muted)]">EMAIL</p>
          <p className="text-white group-hover:text-accent transition-colors break-all">{CONTACT_EMAIL}</p>
        </div>
      </a>
      {Object.values(CLUBS).map((c) => (
        <div key={c.slug} className="hx-card rounded-lg p-6">
          <h3 className="font-display text-xl mb-3">{c.name}</h3>
          <p className="flex items-start gap-2 text-[var(--hx-muted)] text-sm mb-2"><MapPin size={16} className="text-accent mt-0.5 shrink-0" />{c.address}</p>
          <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-[var(--hx-muted)] text-sm hover:text-accent transition-colors"><Phone size={16} className="text-accent shrink-0" />{c.phone}</a>
        </div>
      ))}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errors, setErrors] = useState({});

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateLead(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    // Déploiement statique (sans backend) : ouvre le client mail du visiteur, pré-rempli.
    // TODO (code-ready) : remplacer par un POST vers Formspree/Web3Forms ou le backend si dispo.
    const clubName = CLUBS[form.club]?.name || form.club;
    const subject = `Séance d'essai — ${clubName} (${form.name})`;
    const body = [
      `Nom et prénom : ${form.name}`,
      `Date de naissance : ${form.birth_date}`,
      `Email : ${form.email}`,
      `Téléphone : ${form.phone}`,
      `Adresse : ${form.address}`,
      `Salle : ${clubName}`,
      `Jours préférés : ${form.preferred_days}`,
      `Message : ${form.message}`,
      '',
      '— Envoyé depuis hybrid-training-club.com',
    ].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setStatus('sent');
  };

  const handleReset = () => {
    setStatus('idle');
    setForm(EMPTY_FORM);
    setErrors({});
  };

  return (
    <>
      <ContactHero />
      <section className="pb-16 md:pb-24 max-w-7xl mx-auto px-5 lg:px-8 -mt-6">
        <div className="grid lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-3">
            <div className="hx-card rounded-lg p-7 md:p-10">
              {status === 'sent' ? (
                <SuccessView firstName={form.name.split(' ')[0]} onReset={handleReset} />
              ) : (
                <LeadForm form={form} errors={errors} status={status} onChange={handleChange} onSubmit={handleSubmit} />
              )}
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-2">
            <ContactInfo />
          </Reveal>
        </div>
      </section>
    </>
  );
}
